import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const SWIPE_THRESHOLD = 40;

/**
 * Full-bleed crossfading hero slideshow. Content sits above the slides and never changes.
 *
 * Timing is driven by the active indicator's CSS progress animation (its `animationend` advances the slide),
 * so pausing is a single CSS state and the visible progress line can never drift from the real timer.
 * A JS timer is used as a fallback when indicators are hidden.
 *
 * Attributes:
 * - `autoplay`: advance automatically.
 * - `paused`: paused by the viewer (pause button), or when reduced motion is preferred.
 * - `hover-paused`: temporarily paused (keyboard focus, offscreen, tab hidden, editor selection). Mouse hover does not pause.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [indicators]
 * @property {HTMLButtonElement} [toggle]
 *
 * @extends Component<Refs>
 */
class HeroSlideshowComponent extends Component {
  #index = 0;

  /** @type {number | undefined} */
  #timer;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Set<string>} */
  #holds = new Set();

  #touchStartX = 0;
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.#slides;

    // Make sure exactly one slide is active, whatever the server rendered
    const activeIndex = slides.findIndex((slide) => slide.hasAttribute('data-active'));
    this.#index = Math.max(0, activeIndex);
    slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === this.#index);
      if (i === this.#index) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    if (slides.length < 2) return;

    if (prefersReducedMotion()) this.#setPaused(true);

    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.addEventListener('animationend', this.#onProgressEnd);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#setHold('offscreen', !entry?.isIntersecting);
    });
    this.#observer.observe(this);

    this.#preloadNext();
    this.#schedule();

    // Wait a frame so the first slide's zoom animates in rather than starting at its end state
    requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-ready', '')));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#timer);
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  get #slides() {
    return this.refs.slides ?? [];
  }

  get #usesProgressTimer() {
    const { indicators } = this.refs;
    return Boolean(indicators?.length) && indicators?.[0]?.offsetParent !== null;
  }

  get #isRunning() {
    return this.hasAttribute('autoplay') && !this.hasAttribute('paused') && this.#holds.size === 0;
  }

  /**
   * Go to a slide from an indicator click.
   * @param {string} index
   */
  select(index) {
    this.goTo(Number.parseInt(index, 10));
  }

  /** Pause/play from the toggle button. */
  toggle() {
    this.#setPaused(!this.hasAttribute('paused'));
  }

  next() {
    this.goTo(this.#index + 1);
  }

  previous() {
    this.goTo(this.#index - 1);
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    const slides = this.#slides;
    if (slides.length < 2 || Number.isNaN(index)) return;

    const nextIndex = (index + slides.length) % slides.length;
    if (nextIndex === this.#index) return;

    const current = slides[this.#index];
    const next = slides[nextIndex];
    if (!current || !next) return;

    // Only the outgoing slide stays underneath, so jumping several slides never flashes through the others
    for (const slide of slides) slide.removeAttribute('data-previous');
    current.setAttribute('data-previous', '');
    current.removeAttribute('data-active');
    current.setAttribute('aria-hidden', 'true');

    next.setAttribute('data-active', '');
    next.removeAttribute('aria-hidden');

    this.#index = nextIndex;
    this.#updateIndicators();
    this.#preloadNext();

    // Clear the outgoing slide once the fade has finished
    const fade = Number.parseFloat(getComputedStyle(this).getPropertyValue('--hero-slideshow-fade')) || 0;
    window.setTimeout(() => {
      if (!current.hasAttribute('data-active')) current.removeAttribute('data-previous');
    }, fade + 50);

    this.#schedule();
  }

  #updateIndicators() {
    this.refs.indicators?.forEach((indicator, i) => {
      if (i === this.#index) {
        indicator.setAttribute('aria-current', 'true');
      } else {
        indicator.removeAttribute('aria-current');
      }
    });
  }

  /** Swap lazy loading for eager on the upcoming slide so it's decoded before it fades in. */
  #preloadNext() {
    const slides = this.#slides;
    const upcoming = slides[(this.#index + 1) % slides.length];
    upcoming?.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
  }

  /** Restart the timer (and the CSS progress line) for the current slide. */
  #schedule() {
    window.clearTimeout(this.#timer);

    if (!this.#isRunning) return;

    if (this.#usesProgressTimer) {
      this.#restartProgress();
      return;
    }

    const interval = Number.parseFloat(getComputedStyle(this).getPropertyValue('--hero-slideshow-interval')) || 5;
    this.#timer = window.setTimeout(() => this.next(), interval * 1000);
  }

  #restartProgress() {
    const bar = this.refs.indicators?.[this.#index]?.querySelector('.hero-slideshow__indicator-bar');
    if (!(bar instanceof HTMLElement)) return;

    // Force the ::after animation to start from zero
    bar.style.setProperty('display', 'none');
    void bar.offsetWidth;
    bar.style.removeProperty('display');
  }

  /** @param {AnimationEvent} event */
  #onProgressEnd = (event) => {
    if (event.animationName !== 'hero-slideshow-progress') return;
    if (!this.#isRunning) return;
    this.next();
  };

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.toggleAttribute('paused', paused);

    const { toggle } = this.refs;
    if (toggle) {
      toggle.setAttribute('aria-label', (paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) ?? '');
    }

    if (!paused) this.#schedule();
  }

  /**
   * Temporary pauses. Several can overlap (e.g. keyboard focus and tab hidden), so each is tracked by name.
   * @param {string} name
   * @param {boolean} active
   */
  #setHold(name, active) {
    const wasRunning = this.#isRunning;

    if (active) {
      this.#holds.add(name);
    } else {
      this.#holds.delete(name);
    }

    this.toggleAttribute('hover-paused', this.#holds.size > 0);

    if (!this.#usesProgressTimer) {
      if (wasRunning && !this.#isRunning) window.clearTimeout(this.#timer);
      if (!wasRunning && this.#isRunning) this.#schedule();
    }
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    // Only keyboard focus pauses; a mouse click on an indicator shouldn't stop the slideshow
    if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#setHold('focus', true);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#setHold('focus', false);
  };

  #onVisibilityChange = () => this.#setHold('hidden', document.hidden);

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;

    // Ignore vertical scrolls and taps
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) {
      this.next();
    } else {
      this.previous();
    }
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    this.#setHold('editor', true);

    const slide = event.target.closest('.hero-slideshow__slide');
    const index = slide instanceof HTMLElement ? this.#slides.indexOf(slide) : -1;
    if (index !== -1) this.goTo(index);
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;
    this.#setHold('editor', false);
  };
}

if (!customElements.get('hero-slideshow-component')) {
  customElements.define('hero-slideshow-component', HeroSlideshowComponent);
}
